import { EventEmitter } from 'events';
import { type CombokeysWithGlobalBind, createCombokeys } from './combokeysHelper';
import { type KeyAction, type KeyActionInfo } from './types';

// This makes the `on` and `removeListener` functions we inherit from `EventEmitter`
// typesafe.
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export declare interface ActionsRegistry {
  on(event: 'updated', listener: () => void): this;
  once(event: 'updated', listener: () => void): this;
  removeListener(event: 'updated', listener: () => void): this;
}

/**
 * Keeps track of all registered actions.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class ActionsRegistry extends EventEmitter {
  /**
   * Array of arrays of registered actions.  Whenever `useKeyAction()` calls
   * `registerActions()`, we push the set of registered actions onto this list,
   * and then remove the entire set when it calls `deregisterActions()`.
   */
  private _registeredActions: KeyAction[][] = [];
  private _shortcutHelpVisible: boolean = false;
  private _combokeys: CombokeysWithGlobalBind | undefined;

  constructor() {
    super();

    if (typeof window !== 'undefined') {
      // Don't try to use combokeys if we're using server-side-rendering.
      this._combokeys = createCombokeys(document.documentElement);
    }
  }

  /**
   * Returns true if the shortcut help screen is currently visible.
   */
  get shortcutHelpVisible() {
    return this._shortcutHelpVisible;
  }

  set shortcutHelpVisible(visible: boolean) {
    this._shortcutHelpVisible = visible;
    this.emit('updated');
  }

  // Register an action with Combokeys library.
  private _addActionToCombokeys(action: KeyAction) {
    if (!this._combokeys || action.disabled) {
      return;
    }

    const fn = (event: KeyboardEvent) => {
      const actionDisabled =
        action.disabled || (action.shouldActionRun && !action.shouldActionRun());
      const disabledBecauseHelpVisible = this._shortcutHelpVisible && !action.runWhenHelpShowing;

      if (actionDisabled || disabledBecauseHelpVisible) {
        return;
      }

      action.action(event);

      return !!action.allowEventPropagation;
    };

    if (action.isFormSafe) {
      if (action.keyUp) {
        this._combokeys.bindGlobal(action.shortcut, fn, 'keydown');
        this._combokeys.bindGlobal(action.shortcut, fn, 'keyup');
      } else {
        this._combokeys.bindGlobal(action.shortcut, fn);
      }
    } else {
      if (action.keyUp) {
        this._combokeys.bind(action.shortcut, fn, 'keydown');
        this._combokeys.bind(action.shortcut, fn, 'keyup');
      } else {
        this._combokeys.bind(action.shortcut, fn);
      }
    }
  }

  registerActions(actions: KeyAction[]) {
    this._registeredActions.push(actions);

    // Add actions to combokeys.
    for (const action of actions) {
      this._addActionToCombokeys(action);
    }

    this.emit('updated');
  }

  deregisterActions(actions: KeyAction[]) {
    // Could use splice instead of filter here - performance is similar, though.
    this._registeredActions = this._registeredActions.filter((actionSet) => actionSet !== actions);

    for (const action of actions) {
      if (action.keyUp) {
        this._combokeys?.unbind(action.shortcut, 'keydown');
        this._combokeys?.unbind(action.shortcut, 'keyup');
      } else {
        this._combokeys?.unbind(action.shortcut);
      }
    }

    this.emit('updated');
  }

  /**
   * Deregister all actions in this registry.
   */
  reset() {
    for (const actionSet of this._registeredActions) {
      this.deregisterActions(actionSet);
    }
  }

  /**
   * Returns a list of events to show in the shortcut help.
   */
  getVisisbleKeyboardShortcuts() {
    const actions: { [group: string]: (KeyAction & { info: KeyActionInfo })[] } = {};

    for (const actionSet of this._registeredActions) {
      for (const action of actionSet) {
        if (!action.info) {
          // Skip hidden item.
          continue;
        }
        const { group } = action.info;
        if (!Array.isArray(actions[group])) {
          actions[group] = [];
        }
        const shouldActionRun = action.shouldActionRun?.() ?? true;
        action.disabled = action.disabled || !shouldActionRun;
        actions[group].push(action as KeyAction & { info: KeyActionInfo });
      }
    }
    return actions;
  }
}
